import React, { useRef, useState } from "react";
import { styled, useTheme } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Grid,
  Box,
  Avatar,
  TextField,
  IconButton,
  Theme,
} from "@material-ui/core";
import Button from "../button";
import { IPlayer, IMatch } from "../../types";
import { ActionLink } from "../atoms";
import { injectIntl } from "react-intl";
import messages from "./PageViewMatch.intl";
import PictureUploader from "./PictureUploader";
import VideoList from "./VideoList";
import Typography from "../typography";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import InstagramIcon from "@material-ui/icons/Instagram";
import StarIcon from "@material-ui/icons/Star";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import html2canvas from "html2canvas";
import ShareMatchButton from "./ShareMatchButton";
import SelectMVPModal from "../select-mvp-modal";
import { useMutation } from "@apollo/client";
import { SET_MATCH_MVP, UPDATE_MATCH_SUMMARY } from "./MatchQueries";
import useIsManager from "../../hooks/useIsManager";
import { gql, useQuery } from "@apollo/client";

const CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      id
      role
    }
  }
`;

const ButtonRow = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  paddingTop: theme.spacing(2),
}));

const ScorersSection = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "12px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const ScorerRow = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  background: theme.palette.extra.default,
  borderRadius: "10px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
}));

const PlayerInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flex: 1,
});

const PlayerAvatar = styled(Avatar)(({ theme }: IStyledArguments) => ({
  width: "48px",
  height: "48px",
  background: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "1rem",
}));

const GoalBadge = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  borderRadius: "8px",
  padding: theme.spacing(0.5, 1.5),
  fontWeight: 700,
  minWidth: "50px",
  textAlign: "center",
  fontSize: "1.1rem",
}));

const SectionHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const MVPSection = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  color: "white",
}));

const MVPCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginTop: "16px",
  background: "rgba(255, 255, 255, 0.15)",
  borderRadius: "12px",
  padding: "20px",
});

const MVPAvatar = styled(Avatar)(({ theme }: IStyledArguments) => ({
  width: "80px",
  height: "80px",
  background: "white",
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "1.8rem",
}));

const MVPBadge = styled(Box)({
  position: "absolute",
  top: "-8px",
  right: "-8px",
  background: "#FFD700",
  borderRadius: "50%",
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SummarySection = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SummaryContent = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.default,
  borderRadius: "12px",
  padding: theme.spacing(2.5),
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.extra.borderColor}`,
}));

const SummaryTextField = styled(TextField)(({ theme }: IStyledArguments) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: theme.palette.extra.default,
    "& fieldset": {
      borderColor: theme.palette.extra.borderColor,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const getPlayerInitials = (player: IPlayer): string => {
  const firstInitial = player.name?.charAt(0)?.toUpperCase() || "";
  const lastInitial = player.surname?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

interface IFinalState {
  id: string;
  match: IMatch;
  scorerList: any;
}

const FinalState = injectIntl<IFinalState>(
  ({ id, match, scorerList, intl }) => {
    const theme = useTheme<Theme>();
    const exportRef = useRef<HTMLDivElement>(null);
    const [showMVPModal, setShowMVPModal] = useState(false);
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [summaryText, setSummaryText] = useState(match.matchSummary || "");
    const { data: userData } = useQuery(CURRENT_USER);
    
    const [setMatchMVP] = useMutation(SET_MATCH_MVP, {
      refetchQueries: ["matchDetails"],
      onCompleted: () => {
        console.log("MVP updated successfully");
      },
      onError: (error) => {
        console.error("Error setting MVP:", error);
      },
    });

    const [updateMatchSummary] = useMutation(UPDATE_MATCH_SUMMARY, {
      refetchQueries: ["matchDetails"],
      onCompleted: () => {
        console.log("Match summary updated successfully");
        setIsEditingSummary(false);
      },
      onError: (error) => {
        console.error("Error updating match summary:", error);
      },
    });

    // Check if current user is manager or coach
    const canSelectMVP = userData?.currentUser && useIsManager(userData.currentUser.role);

    // Filter and sort scorers
    const scorers = match.playersConfirmed?.filter(
      (player: IPlayer) => scorerList[player.id] > 0
    ) || [];
    
    const sortedScorers = scorers.sort(
      (a: IPlayer, b: IPlayer) => scorerList[b.id] - scorerList[a.id]
    );

    const totalGoals = Object.values(scorerList).reduce(
      (acc: number, goals: any) => acc + (goals || 0),
      0
    );

    const exportToInstagram = async () => {
      if (!exportRef.current) return;

      try {
        const canvas = await html2canvas(exportRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          width: 1080,
          height: 1080,
        });

        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `match-summary-${match.homeTeam?.name || 'match'}-${Date.now()}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
          }
        });
      } catch (error) {
        console.error('Error exporting to Instagram:', error);
      }
    };

    const handleSelectMVP = (playerId: string) => {
      setMatchMVP({
        variables: {
          matchId: id,
          playerId: playerId,
        },
      });
    };

    const handleSaveSummary = () => {
      if (summaryText.trim()) {
        updateMatchSummary({
          variables: {
            matchId: id,
            matchSummary: summaryText.trim(),
          },
        });
      }
    };

    const handleCancelEdit = () => {
      setSummaryText(match.matchSummary || "");
      setIsEditingSummary(false);
    };

    return (
      <>
        {/* Goal Scorers Section - New Visual */}
        {sortedScorers.length > 0 && (
          <Grid item xs={12}>
            <ScorersSection>
              <SectionHeader>
                <EmojiEventsIcon style={{ fontSize: "1.5rem" }} />
                <Typography.H6 style={{ margin: 0, fontWeight: 700 }}>
                  Match Scorers
                </Typography.H6>
                <Typography.Body
                  style={{
                    marginLeft: "auto",
                    fontWeight: 600,
                    color: theme.palette.text.secondary
                  }}
                >
                  {totalGoals} {totalGoals === 1 ? "goal" : "goals"}
                </Typography.Body>
              </SectionHeader>

              {sortedScorers.map((player: IPlayer, index: number) => (
                <ScorerRow key={player.id}>
                  <PlayerInfo>
                    <Box position="relative">
                      <PlayerAvatar>
                        {getPlayerInitials(player)}
                      </PlayerAvatar>
                      {index === 0 && scorerList[player.id] > 1 && (
                        <Box
                          style={{
                            position: "absolute",
                            top: "-4px",
                            right: "-4px",
                            fontSize: "1.2rem",
                          }}
                        >
                          👑
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Typography.Body 
                        style={{ 
                          fontWeight: 700,
                          marginBottom: "2px",
                        }}
                      >
                        {player.name} {player.surname}
                      </Typography.Body>
                      <Typography.Body
                        style={{
                          fontSize: "0.85rem",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {player.position || "Jugador"}
                        {player.number && ` • #${player.number}`}
                      </Typography.Body>
                    </Box>
                  </PlayerInfo>
                  <GoalBadge>
                    {scorerList[player.id]} ⚽
                  </GoalBadge>
                </ScorerRow>
              ))}

              {match.ownGoals > 0 && (
                <ScorerRow style={{ background: theme.palette.extra.lossSoft }}>
                  <PlayerInfo>
                    <Avatar
                      style={{
                        backgroundColor: theme.palette.extra.loss,
                        width: "48px",
                        height: "48px",
                      }}
                    >
                      AG
                    </Avatar>
                    <Box>
                      <Typography.Body style={{ fontWeight: 700 }}>
                        {intl.formatMessage(messages.ownGoal)}
                      </Typography.Body>
                      <Typography.Body
                        style={{
                          fontSize: "0.85rem",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Own Goal
                      </Typography.Body>
                    </Box>
                  </PlayerInfo>
                  <GoalBadge style={{ background: theme.palette.extra.loss }}>
                    {match.ownGoals} ⚽
                  </GoalBadge>
                </ScorerRow>
              )}
            </ScorersSection>
          </Grid>
        )}

        {/* MVP Section */}
        {match.mvp && (
          <Grid item xs={12}>
            <MVPSection>
              <SectionHeader style={{ color: "white", marginBottom: "8px" }}>
                <StarIcon style={{ fontSize: "2rem", color: "#FFD700" }} />
                <Typography.H6 style={{ margin: 0, fontWeight: 700, color: "white" }}>
                  Match MVP
                </Typography.H6>
              </SectionHeader>
              <MVPCard>
                <Box position="relative">
                  <MVPAvatar>
                    {getPlayerInitials(match.mvp)}
                  </MVPAvatar>
                  <MVPBadge>
                    <StarIcon style={{ fontSize: "20px", color: "#fff" }} />
                  </MVPBadge>
                </Box>
                <Box flex="1">
                  <Typography.H5 style={{ margin: 0, fontWeight: 700, color: "white" }}>
                    {match.mvp.name} {match.mvp.surname}
                  </Typography.H5>
                  <Typography.Body style={{ color: "rgba(255, 255, 255, 0.9)", marginTop: "4px" }}>
                    {match.mvp.position || "Player"}
                    {match.mvp.number && ` • #${match.mvp.number}`}
                  </Typography.Body>
                  {scorerList[match.mvp.id] > 0 && (
                    <Typography.Body style={{ color: "rgba(255, 255, 255, 0.9)", marginTop: "4px" }}>
                      ⚽ {scorerList[match.mvp.id]} {scorerList[match.mvp.id] === 1 ? "goal" : "goals"}
                    </Typography.Body>
                  )}
                </Box>
              </MVPCard>
            </MVPSection>
          </Grid>
        )}

        {/* Select MVP Button - Only for Manager/Coach */}
        {canSelectMVP && match.playersConfirmed && match.playersConfirmed.length > 0 && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" marginBottom="16px">
              <Button
                onClick={() => setShowMVPModal(true)}
                variant="contained"
                color="primary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#FFD700",
                  color: "#3d2b00",
                  fontWeight: 700,
                }}
              >
                <StarIcon />
                {match.mvp ? "Change Match MVP" : "Select Match MVP"}
              </Button>
            </Box>
          </Grid>
        )}

        {/* Coach Summary Section */}
        <Grid item xs={12}>
          <SummarySection>
            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
              <SectionHeader>
                <DescriptionIcon style={{ fontSize: "1.5rem" }} />
                <Typography.H6 style={{ margin: 0, fontWeight: 700 }}>
                  {intl.formatMessage(messages.matchSummaryTitle)}
                </Typography.H6>
              </SectionHeader>
              {canSelectMVP && !isEditingSummary && (
                <IconButton
                  onClick={() => setIsEditingSummary(true)}
                  size="small"
                  style={{ color: theme.palette.primary.main }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            {isEditingSummary ? (
              <Box>
                <SummaryTextField
                  multiline
                  rows={6}
                  variant="outlined"
                  placeholder={intl.formatMessage(messages.matchSummaryPlaceholder)}
                  value={summaryText}
                  onChange={(e) => setSummaryText(e.target.value)}
                />
                <Box display="flex" marginTop="16px" justifyContent="flex-end" style={{ gap: "12px" }}>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outlined"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <CloseIcon />
                    {intl.formatMessage(messages.matchSummaryCancel)}
                  </Button>
                  <Button
                    onClick={handleSaveSummary}
                    variant="contained"
                    color="primary"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <SaveIcon />
                    {intl.formatMessage(messages.matchSummarySave)}
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {match.matchSummary ? (
                  <SummaryContent>
                    <Typography.Body style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                      {match.matchSummary}
                    </Typography.Body>
                  </SummaryContent>
                ) : (
                  canSelectMVP && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      padding="32px"
                      style={{
                        border: `2px dashed ${theme.palette.extra.borderColor}`,
                        borderRadius: "12px",
                        background: theme.palette.extra.backgroundGrey,
                      }}
                    >
                      <DescriptionIcon style={{ fontSize: "48px", color: theme.palette.text.secondary, marginBottom: "16px" }} />
                      <Typography.Body style={{ color: theme.palette.text.secondary, marginBottom: "16px", textAlign: "center" }}>
                        {intl.formatMessage(messages.matchSummaryPlaceholder)}
                      </Typography.Body>
                      <Button
                        onClick={() => setIsEditingSummary(true)}
                        variant="outlined"
                        color="primary"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <EditIcon />
                        {intl.formatMessage(messages.matchSummaryAdd)}
                      </Button>
                    </Box>
                  )
                )}
              </>
            )}
          </SummarySection>
        </Grid>

        <Grid item xs={12}>
          <PictureUploader id={id} />
        </Grid>
        <Grid item xs={12}>
          <VideoList matchId={id} />
        </Grid>
        <Grid item xs={12}>
          <ButtonRow style={{ marginBottom: "30px", gap: "16px", flexWrap: "wrap" }}>
            <ShareMatchButton match={match} scorerList={scorerList} />
            <Button 
              onClick={exportToInstagram}
              style={{ 
                background: "linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)",
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minHeight: "36px",
                padding: "6px 16px",
              }}
            >
              <InstagramIcon />
              Export for Instagram
            </Button>
            <ActionLink to={`/people/matches`}>
              <Button 
                style={{
                  minHeight: "36px",
                  padding: "6px 16px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {intl.formatMessage(messages.backMatches)}
              </Button>
            </ActionLink>
          </ButtonRow>
        </Grid>

        {/* Hidden export container for Instagram */}
        <div 
          ref={exportRef}
          style={{
            width: "1080px",
            height: "1080px",
            padding: "60px",
            background: "linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)",
            display: "flex",
            flexDirection: "column",
            position: "absolute",
            left: "-9999px",
            top: 0,
          }}
        >
          <Box style={{ 
            background: "white", 
            borderRadius: "24px", 
            padding: "48px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}>
            {/* Header with Logo */}
            <Box style={{ 
              display: "flex", 
              justifyContent: "center",
              marginBottom: "32px",
            }}>
              <img 
                src="/images/footik-logo.svg"
                alt="Footik"
                style={{ height: "56px" }}
              />
            </Box>

            {/* Match Title */}
            <Typography.H4 style={{ 
              textAlign: "center",
              marginBottom: "24px",
              color: "#00bcd4",
              fontWeight: 700,
            }}>
              Match Summary
            </Typography.H4>

            {/* Teams and Score */}
            <Box style={{ 
              display: "flex", 
              justifyContent: "space-around",
              alignItems: "center",
              marginBottom: "48px",
              padding: "32px",
              background: "linear-gradient(135deg, rgba(0, 188, 212, 0.1) 0%, rgba(0, 151, 167, 0.1) 100%)",
              borderRadius: "16px",
            }}>
              <Typography.H5 style={{ 
                flex: 1, 
                textAlign: "center",
                fontWeight: 700,
                fontSize: "32px",
              }}>
                {match.isHomeTeamLocal ? match.homeTeam.name : match.awayTeam}
              </Typography.H5>
              
              <Box style={{ 
                display: "flex", 
                gap: "24px",
                alignItems: "center",
              }}>
                <Typography.H3 style={{ 
                  fontWeight: 700,
                  fontSize: "56px",
                  color: "#00bcd4",
                }}>
                  {match.score?.home || 0}
                </Typography.H3>
                <Typography.H5 style={{ color: "#999", fontSize: "32px" }}>-</Typography.H5>
                <Typography.H3 style={{ 
                  fontWeight: 700,
                  fontSize: "56px",
                  color: "#00bcd4",
                }}>
                  {match.score?.away || 0}
                </Typography.H3>
              </Box>

              <Typography.H5 style={{ 
                flex: 1, 
                textAlign: "center",
                fontWeight: 700,
                fontSize: "32px",
              }}>
                {match.isHomeTeamLocal ? match.awayTeam : match.homeTeam.name}
              </Typography.H5>
            </Box>

            {/* Scorers */}
            {sortedScorers.length > 0 && (
              <Box>
                <Box style={{ 
                  display: "flex", 
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "24px",
                }}>
                  <EmojiEventsIcon style={{ fontSize: "36px", color: "#00bcd4" }} />
                  <Typography.H5 style={{ 
                    fontWeight: 700,
                    fontSize: "28px",
                  }}>
                    Goal Scorers
                  </Typography.H5>
                </Box>

                {sortedScorers.map((player: IPlayer) => (
                  <Box 
                    key={player.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "16px 24px",
                      marginBottom: "12px",
                      background: "rgba(0, 188, 212, 0.05)",
                      borderRadius: "12px",
                      borderLeft: "4px solid #00bcd4",
                    }}
                  >
                    <Typography.Body style={{ 
                      fontSize: "24px",
                      fontWeight: 600,
                    }}>
                      {player.name} {player.surname}
                    </Typography.Body>
                    <Typography.Body style={{ 
                      fontSize: "28px",
                      fontWeight: 700,
                      color: "#00bcd4",
                    }}>
                      {scorerList[player.id]} ⚽
                    </Typography.Body>
                  </Box>
                ))}
              </Box>
            )}

            {/* Date */}
            <Box style={{ 
              marginTop: "auto",
              paddingTop: "32px",
              textAlign: "center",
              borderTop: "2px solid #e0e0e0",
            }}>
              <Typography.Body style={{ 
                color: "#999",
                fontSize: "20px",
              }}>
                {match.date}
              </Typography.Body>
            </Box>
          </Box>
        </div>

        {/* MVP Selection Modal */}
        <SelectMVPModal
          open={showMVPModal}
          players={match.playersConfirmed || []}
          scorerList={scorerList}
          currentMVP={match.mvp?.id}
          onClose={() => setShowMVPModal(false)}
          onConfirm={handleSelectMVP}
        />
      </>
    );
  }
);

export default FinalState;
